'use client';

import { EntitlementsProvider } from '@contexts/EntitlementsContext';
import { useMicrocopy } from '@contexts/MicrocopyContext';
import {
  ProductListsProvider,
  useProductLists,
} from '@contexts/ProductListsContext';
import type { Result } from '@coveo/headless-react/ssr';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { useEffect, useRef } from 'react';
import { ListingEmptyState } from '../ListingEmptyState';
import { ProductCardSkeleton } from '../Skeleton/ProductCardSkeleton';
import { ResultCard } from './ResultCard';
import { useSearchControllers } from './headless/SearchDefinitionContext';
import { parseProductId } from './parseProductId';
import { useSearchLoading } from './useSearchLoading';

// The 13rem floor guards the card layout's minimum comfortable width.
const GRID_CLASSES =
  'grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(18rem,1fr))] min-[430px]:[grid-template-columns:repeat(auto-fill,minmax(13rem,1fr))] lg:[grid-template-columns:repeat(auto-fill,minmax(14rem,1fr))]';

const SKELETON_COUNT = 8;

export type ResultListProps = {
  className?: string;
};

export const ResultList = ({ className }: ResultListProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
  }>();
  const controllers = useSearchControllers();
  const { state } = controllers.useResultList();
  const { isInitialLoad } = useSearchLoading();

  if (isInitialLoad) {
    return (
      <div className={`${GRID_CLASSES} ${className ?? ''}`}>
        {Array.from({ length: SKELETON_COUNT }, (_, i) => (
          <ProductCardSkeleton key={`skeleton-${i}`} />
        ))}
      </div>
    );
  }

  if (!state.results || state.results.length === 0) {
    return (
      <ListingEmptyState
        title={getMicrocopyValue(
          searchLabelsMicrocopy,
          'search.no-results-title',
          'No results found'
        )}
        description={getMicrocopyValue(
          searchLabelsMicrocopy,
          'search.no-results-suggestion',
          'Try different keywords or remove filters'
        )}
      />
    );
  }

  return (
    <EntitlementsProvider>
      <ProductListsProvider>
        <ResultListInner results={state.results} className={className} />
      </ProductListsProvider>
    </EntitlementsProvider>
  );
};

type ResultListInnerProps = {
  results: Result[];
  className?: string;
};

const ResultListInner = ({ results, className }: ResultListInnerProps) => {
  const { fetchBatchProductMembership } = useProductLists();
  const prevKeyRef = useRef('');

  useEffect(() => {
    const productIds = results
      .map((r) => parseProductId(r.raw.ec_product_id))
      .filter(Boolean);

    const key = productIds.join(',');
    if (key && key !== prevKeyRef.current) {
      prevKeyRef.current = key;
      fetchBatchProductMembership(productIds);
    }
  }, [results, fetchBatchProductMembership]);

  return (
    <ul
      data-test="search-results-list"
      className={`${GRID_CLASSES} ${className ?? ''}`}
    >
      {results.map((result) => (
        <li key={parseProductId(result.raw.ec_product_id) || result.uniqueId}>
          <ResultCard result={result} />
        </li>
      ))}
    </ul>
  );
};
